import {
  type FC,
  useRef,
  useEffect,
  type DetailedHTMLProps,
  type HTMLAttributes,
} from "react"
import WebViewer from "@pdftron/webviewer"
import {config} from "../../config"

type Props = {
  deliverableId?: string
  file?: string | File | Blob
  fileType?: string
  fileName?: string
  disableHeader?: boolean
  fullHeight?: boolean
  handleReset?: () => void
  handleDownload?: () => void
  disabled?: boolean
  containerProps?: DetailedHTMLProps<
    HTMLAttributes<HTMLDivElement>,
    HTMLDivElement
  >
}

const DocViewer: FC<Props> = (props) => {
  const {
    file,
    fileName,
    handleReset,
    fileType,
    deliverableId,
    handleDownload,
    disableHeader,
    disabled,
    fullHeight,
    containerProps,
  } = props
  const {apryseKey} = config

  const viewer = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!viewer.current) return
    let tempUrl = ""
    window.localStorage.removeItem("init_timestamp")
    WebViewer(
      {
        path: "/webviewer/lib",
        css: "/webviewer/styles.css",
        licenseKey:
          // deepcode ignore HardcodedNonCryptoSecret: Secret is not hardcoded, that is just the name of the key
          apryseKey || "Insert commercial license key here after purchase",
        initialDoc:
          typeof file === "string" && fileType !== "mp4" ? file : undefined,
        disabledElements: [
          "leftPanelButton",
          "panToolButton",
          "selectToolButton",
          "viewControlsButton",
          "toolsHeader",
          "settingsButton",
          "menuButton",
          disableHeader ? "header" : "",
        ],
      },
      viewer.current!,
    ).then(async (instance): Promise<any> => {
      const {UI, Core} = instance
      Core.disableLogs(true)
      Core.disableEmbeddedJavaScript()
      if (file && typeof file !== "string" && fileType !== "mp4") {
        UI.loadDocument(file, {filename: fileName, extension: fileType})
      }
      if (
        fileType === "csv" ||
        (file && typeof file === "string" && file.includes(".csv"))
      ) {
        UI.disableElements(["errorModal", "progressModal"])
        UI.showWarningMessage({
          title: "Error Loading Document",
          message: "Please be aware the preview of this file is not supported",
          confirmBtnText: "OK",
          onConfirm: () => {
            return Promise.resolve()
          },
          onCancel: () => {
            return Promise.resolve()
          },
        })
      }
      UI.disableTools()
      UI.disableFeatures([
        UI.Feature.SideBySideView,
        UI.Feature.WatermarkPanel,
        UI.Feature.PageNavigation,
        UI.Feature.Print,
        UI.Feature.MultiTab,
        UI.Feature.Search,
        UI.Feature.NotesPanel,
        UI.Feature.Ribbons,
        UI.Feature.FilePicker,
        UI.Feature.ContentEdit,
        UI.Feature.Portfolio,
        UI.Feature.ComparePages,
        UI.Feature.Redaction,
        UI.Feature.TextSelection,
        UI.Feature.SideBySideView,
        UI.Feature.ContentEdit,
      ])
      // UI.setZoomLevel("100%")
      UI.setFitMode(UI.FitMode.FitWidth)
      UI.setLayoutMode(UI.LayoutMode.Continuous)
      UI.setHeaderItems((header) => {
        header.get("menuButton").insertBefore({
          type: "actionButton",
          title: "Download",
          img: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="currentColor" d="M16.59 9H15V4c0-.55-.45-1-1-1h-4c-.55 0-1 .45-1 1v5H7.41c-.89 0-1.34 1.08-.71 1.71l4.59 4.59c.39.39 1.02.39 1.41 0l4.59-4.59c.63-.63.19-1.71-.7-1.71zM5 19c0 .55.45 1 1 1h12c.55 0 1-.45 1-1s-.45-1-1-1H6c-.55 0-1 .45-1 1z"/></svg>',
          onClick: async () => {
            if (deliverableId && handleDownload) {
              handleDownload()
              return
            }
            if (file && typeof file !== "string") {
              const link = document.createElement("a")
              const fileUrl = URL.createObjectURL(file)
              link.href = fileUrl
              link.download = `${fileName || "file"}.${fileType || "pdf"}`
              link.target = "_blank"
              link.rel = "noopener noreferrer"
              link.click()
              link.remove()
            } else {
              const download = await fetch(file as string).then((res) =>
                res.blob(),
              )
              const a = document.createElement("a")
              a.href = URL.createObjectURL(download)
              a.download = `${fileName || "file"}.${fileType || "pdf"}`
              a.target = "_blank"
              a.rel = "noopener noreferrer"
              a.click()
              a.remove()
            }
          },
        })

        if (!disabled) {
          header.get("searchButton").insertAfter({
            type: "actionButton",
            title: "Close",
            img: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="currentColor" d="M18.3 5.71a.996.996 0 0 0-1.41 0L12 10.59L7.11 5.7A.996.996 0 1 0 5.7 7.11L10.59 12L5.7 16.89a.996.996 0 1 0 1.41 1.41L12 13.41l4.89 4.89a.996.996 0 1 0 1.41-1.41L13.41 12l4.89-4.89c.38-.38.38-1.02 0-1.4z"/></svg>',
            onClick: () => {
              if (handleReset) {
                handleReset()
              }
            },
          })
        }
      })
    })

    return () => {
      viewer.current = null
      if (tempUrl) {
        URL.revokeObjectURL(tempUrl)
      }
    }
    //eslint-disable-next-line
  }, [file, fileName, handleReset, apryseKey, fileType])
  return (
    <div
      className="webviewer"
      ref={viewer}
      {...containerProps}
      style={{
        width: "100%",
        height: fullHeight ? "100%" : "40rem",
        position: "relative",
        ...containerProps?.style,
      }}
    ></div>
  )
}

DocViewer.displayName = "DocViewer"

export default DocViewer
